import type { ReactNode } from "react";

export interface Column<T> {
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** extra className on the <td> (e.g. "mono", "num") */
  className?: string;
  /** right-align this column (numbers) */
  right?: boolean;
  width?: string | number;
}

/**
 * Generic data table over the shared `.tbl` primitive. Columns are declared once; rows render
 * uniformly. Optional row click + per-row className (e.g. a flash on live update). Renders the
 * provided `empty` node when there are no rows.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  empty,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  empty?: ReactNode;
}) {
  if (rows.length === 0 && empty != null) return <>{empty}</>;
  return (
    <table className="tbl">
      <thead>
        <tr>
          {columns.map((c, i) => (
            <th key={i} style={{ width: c.width, textAlign: c.right ? "right" : undefined }}>
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={rowKey(r)}
            className={rowClassName?.(r)}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            style={onRowClick ? { cursor: "pointer" } : undefined}
          >
            {columns.map((c, i) => (
              <td key={i} className={c.className} style={{ textAlign: c.right ? "right" : undefined }}>
                {c.cell(r)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
